// src/pages/work/WorkDetails.ui.jsx
// Shell (outer layer) for Work Details — orchestrates Header, MainBody, Sidebar.
// Presentational only: NO data fetching/toasts here; all IO handled by WorkDetails.page.jsx.

import React, { useMemo } from "react";
import WorkDetailsHeader from "./WorkDetails.ui.header";
import WorkDetailsMainBody from "./WorkDetails.ui.mainbody";
import WorkDetailsSidebar from "./WorkDetails.ui.sidebar";

/* ----------------------------- local helpers ------------------------------ */
const fmtDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

/* ----------------------------------- Shell --------------------------------- */
export default function WorkDetailsUI(props) {
  const {
    // data
    work,
    resolving,
    isLoading,
    isError,
    softDeleted,
    canMutate,
    role,

    // NEW: client/bill-to info (computed in page)
    billTo,              // { company, contactName, email, phone, addressText, clientId }
    client,              // live client payload (optional, for links/tooltips)
    loadingClient,       // boolean

    // header/meta actions
    onBack,

    // saves
    onSaveBasics,
    onSaveSchedule,  // (header autosave schedule editor)
    onSaveTags,
    onSaveDescription,
    onSaveAddress,   // used by MainBody as onSaveAddress
    onSaveAssignees,

    // status
    onTransition,
    transitioning,
    primaryNext,

    // destructive
    onDelete,
    deleting,

    // open entity
    onOpenEntity,

    // children lists
    childTasks = [],
    childAppointments = [],
    loadingChildTasks,
    loadingChildAppointments,

    // NEW inline child linker handlers expected by MainBody
    onChildSearch,              // ({ type, q }) => Promise<array>
    onLinkChild,                // ({ childId }) => Promise<void>
    onCreateChild,              // ({ type, prefill }) => void
    onUnlinkChild,

    // legacy (kept only to avoid breakage if provided; not used by new UI)
    onLinkExistingChild,        // old name → we’ll pass it if onLinkChild missing

    // flags
    savingAny,

    // optional: job profile save (if provided, enables inline edit)
    onSaveJobProfile, // (jobProfilePatch) => Promise<void)

    // ✅ ONLY modal trigger (preferred path)
    onRequestSend,           // (type: 'confirm'|'reminder'|'complete') => void

    // Header interactive options
    assigneeOptions = [],          // [{ id, name, email }]
    savingBasics,                  // optional spinner state
    savingAssignees,               // optional spinner state

    // Sidebar needs these for Linked Records / actions
    onCreateInvoice,          // () => void
    converting,               // boolean (quote→invoice conversion busy)
    attachingInvoice,         // boolean (work↔invoice link busy)
  } = props;

  const canEdit = canMutate && !softDeleted;

  // When label for header (unchanged)
  const whenLabel = useMemo(() => {
    const s = work?.schedule || {};
    if (!s?.start && !s?.end) return "Not scheduled";
    if (s?.allDay) {
      return `${new Date(s.start).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" })} (All day) • ${s.timezone || "America/Toronto"}`;
    }
    const startStr = s?.start ? fmtDateTime(s.start) : "—";
    const endStr = s?.end ? fmtDateTime(s.end) : "—";
    return `${startStr} – ${endStr} • ${s.timezone || "America/Toronto"}`;
  }, [work?.schedule]);

  return (
    <div className="w-full min-h-[90vh] py-8 px-3 sm:px-6 lg:px-8 bg-slate-50 print:bg-white">
      <div className="w-full rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden print:border-0 print:shadow-none">
        {/* Header — component provides its own padding */}
        <WorkDetailsHeader
          work={work}
          resolving={resolving}
          isLoading={isLoading}
          isError={isError}
          softDeleted={softDeleted}
          canMutate={canMutate}
          role={role}
          onBack={onBack}
          onOpenEntity={onOpenEntity}
          // status actions
          onTransition={onTransition}
          transitioning={transitioning}
          primaryNext={primaryNext}
          // destructive
          onDelete={onDelete}
          deleting={deleting}
          // ✅ preferred: open SendWorkModal ONLY (no direct-send)
          onRequestSend={onRequestSend}
          // header inline editors
          onSaveBasics={onSaveBasics}
          onSaveAssignees={onSaveAssignees}
          assigneeOptions={assigneeOptions}
          savingBasics={savingBasics}
          savingAssignees={savingAssignees}
          // schedule autosave editor (new pass-through)
          onSaveSchedule={onSaveSchedule}
          schedule={work?.schedule}
          // helper
          whenLabel={whenLabel}
        />

        {/* Body */}
        <div className="p-4 sm:p-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            {/* MAIN (2/3) */}
            <div className="lg:col-span-2">
              <WorkDetailsMainBody
                // data/flags
                work={work}
                resolving={resolving}
                isLoading={isLoading}
                isError={isError}
                softDeleted={softDeleted}
                canMutate={canMutate}
                savingAny={savingAny}

                // client/bill-to info for the Client & Location card
                billTo={billTo}
                client={client}
                loadingClient={loadingClient}

                // handlers
                onOpenEntity={onOpenEntity}
                onSaveBasics={onSaveBasics}
                onSaveDescription={onSaveDescription}
                onSaveJobProfile={onSaveJobProfile}
                onSaveAddress={onSaveAddress}

                // inline child linkers
                onChildSearch={onChildSearch}
                onLinkChild={onLinkChild || onLinkExistingChild}
                onCreateChild={onCreateChild}
                onUnlinkChild={onUnlinkChild}

                // children
                childTasks={childTasks}
                childAppointments={childAppointments}
                loadingChildTasks={loadingChildTasks}
                loadingChildAppointments={loadingChildAppointments}
              />
            </div>

            {/* SIDEBAR (1/3) */}
            <div className="lg:col-span-1">
              <WorkDetailsSidebar
                work={work}
                canMutate={canMutate}
                softDeleted={softDeleted}
                // nav/openers
                onOpenEntity={onOpenEntity}
                // linked records + actions
                onCreateInvoice={onCreateInvoice}
                onDelete={onDelete}
                deleting={deleting}
                converting={converting}
                attachingInvoice={attachingInvoice}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
